import {
    AutomaticUpdatesIcon,
    CrossIcon,
    Heading,
    Pane,
    Text,
    TickCircleIcon,
    toaster,
} from 'evergreen-ui';
import React, { useCallback, useMemo, useState } from 'react';
import Course from '../../../../../../../workflows_new/src/types/models/Course';
import styles from './styles.module.css';
import { Button } from '../../../_ui/Buttons';
import { FileAnswer, Record, Result, StudentAnswers } from './types';
import { useCademyAPIMutation } from 'services/CademyAPI/useCademyAPIMutation';
import { MultipleChoiceQuestion } from './Questions/MultipleChoiceQuestion';
import { ShortAnswerQuestion } from './Questions/ShortAnswerQuestion';
import { NumberQuestion } from './Questions/NumberQuestion';
import { LongTextQuestion } from './Questions/LongTextQuestion';
import { FileUploadQuestion } from './Questions/FileUploadQuestion';
import { Lesson, QuizContentBlock } from '@shared/types';

const createdAtDesc = (a: Result, b: Result) => {
    if (a.created_at > b.created_at) return -1;
    if (a.created_at < b.created_at) return 1;
    return 0;
};

type QuizContentProps = {
    content: QuizContentBlock;
    record: Record;
    recordType: 'booking' | 'order';
    lesson: Lesson & { quiz_responses: Result[] };
    course: Course.Model;
    refreshData: () => void;
};
export const QuizContent = ({
    content,
    record,
    recordType,
    lesson,
    course,
    refreshData,
}: QuizContentProps) => {
    const savedResult: Result | undefined = useMemo(
        () =>
            lesson.quiz_responses
                .sort(createdAtDesc)
                .find((response) => response.quiz_ref === content._id),
        [lesson, content]
    );
    const savedAnswers = useMemo(
        () => (savedResult ? savedResult.submitted_answers : false),
        [savedResult]
    );
    const [studentAnswers, setStudentAnswers] = useState<StudentAnswers>(
        savedAnswers ? savedAnswers : {}
    );
    const [lastAutoSave, setLastAutoSave] = useState<number>(0);

    const {
        data: submittedQuizResult,
        mutateAsync: submitQuizResponse,
        isLoading: isSubmitting,
    } = useCademyAPIMutation('SubmitQuizResponse');
    const { mutate: submitDraftQuizResponse } = useCademyAPIMutation('SubmitDraftQuizResponse', {
        onSuccess: () => {
            setLastAutoSave(Date.now());
        },
        onError: (error: any) => {
            if (error?.response?.data?.code === 'quiz-response/max-retakes-reached') {
                toaster.danger(error.response.data.message || 'Maximum retakes reached');
                refreshData();
            }
        },
    });

    const result = useMemo(() => {
        if (submittedQuizResult) {
            return submittedQuizResult;
        }
        return savedResult;
    }, [submittedQuizResult, savedResult]);

    const remainingAttempts = useMemo(() => {
        if (!content.allow_retakes || typeof content.max_retakes !== 'number') return null;

        const failedAttempts = lesson.quiz_responses.filter(
            (response) =>
                response.quiz_ref === content._id &&
                response.marked === true &&
                response.result?.pass === false
        ).length;

        return Math.max(0, content.max_retakes - failedAttempts);
    }, [content.allow_retakes, content.max_retakes, content._id, lesson.quiz_responses]);

    const maxRetakesReached = useMemo(() => {
        if (!content.allow_retakes || typeof content.max_retakes !== 'number') return false;

        const failedAttempts = lesson.quiz_responses.filter(
            (response) =>
                response.quiz_ref === content._id &&
                response.marked === true &&
                response.result?.pass === false
        ).length;

        return failedAttempts >= content.max_retakes;
    }, [content.allow_retakes, content.max_retakes, content._id, lesson.quiz_responses]);

    const handleAutoSave = useCallback(async () => {
        try {
            submitDraftQuizResponse({
                body: {
                    answers: studentAnswers,
                    record_type: recordType,
                    record_ref: record._id,
                    course_ref: course._id,
                    lesson_ref: lesson._id,
                    quiz_ref: content._id,
                },
            });
        } catch (error) {
            console.error(error);
        }
    }, [
        submitDraftQuizResponse,
        studentAnswers,
        recordType,
        record._id,
        course._id,
        lesson._id,
        content._id,
    ]);

    const handleSubmit = useCallback(async () => {
        submitQuizResponse(
            {
                body: {
                    answers: studentAnswers,
                    record_type: recordType,
                    record_ref: record._id,
                    course_ref: course._id,
                    lesson_ref: lesson._id,
                    quiz_ref: content._id,
                },
            },
            {
                onSuccess: (data) => {
                    if (data.marked === true) {
                        if (data.result?.pass === true) {
                            toaster.success('Quiz passed!');
                            refreshData();
                        } else {
                            toaster.warning('Quiz failed.');
                            refreshData();
                        }
                    }
                },
                onError: (error: any) => {
                    const errorMessage =
                        error?.response?.data?.message ||
                        'An error occurred while submitting the quiz';
                    toaster.danger(errorMessage);
                    refreshData();
                },
            }
        );
    }, [
        submitQuizResponse,
        studentAnswers,
        recordType,
        record._id,
        course._id,
        lesson._id,
        content._id,
        refreshData,
    ]);

    const displayResult = useCallback(() => {
        if (!result || result.draft === true) return null;

        if (result.marked !== true || !result.result) {
            return (
                <Pane className={styles.results_container}>
                    <AutomaticUpdatesIcon size={32} />
                    <Heading>Answers Submitted</Heading>
                    <Text>Someone will mark them shortly.</Text>
                </Pane>
            );
        }

        const { pass } = result.result;

        return (
            <Pane
                className={styles.results_container}
                style={{ background: pass ? 'var(--green25)' : 'var(--red25)' }}
            >
                {pass ? (
                    <TickCircleIcon color="var(--green)" size={32} />
                ) : (
                    <CrossIcon color="var(--red)" size={32} />
                )}
                <Heading>Quiz {pass ? 'Passed' : 'Failed'}</Heading>
                <Heading size={400}>You scored {Math.round(result.result.percentage)}%</Heading>
                <Text>
                    You answered correctly {result.result.totalCorrect} out of{' '}
                    {result.result.totalPossible} questions.
                </Text>
                {pass ? (
                    <Text color="green">Well Done!</Text>
                ) : (
                    <>
                        <Text color="red">You needed {content.pass_mark}% to pass.</Text>
                        {content.allow_retakes &&
                            content.max_retakes &&
                            typeof content.max_retakes === 'number' &&
                            remainingAttempts !== null && (
                                <Text marginTop={8} fontWeight={500}>
                                    {remainingAttempts > 0
                                        ? `You have ${remainingAttempts} ${remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining.`
                                        : 'You have no more attempts remaining.'}
                                </Text>
                            )}
                    </>
                )}
                {result.result?.assessorFeedback && <Text>{result.result.assessorFeedback}</Text>}
            </Pane>
        );
    }, [result, content.pass_mark, content.allow_retakes, content.max_retakes, remainingAttempts]);

    const quizDisabled = useMemo(() => {
        if (result && result.marked === false && result.draft === false) return true;
        if (result && result.marked === true && result.result?.pass === true) return true;
        if (result && result.marked === true && content.allow_retakes !== true) return true;

        if (
            content.allow_retakes &&
            content.max_retakes &&
            typeof content.max_retakes === 'number'
        ) {
            const failedAttempts = lesson.quiz_responses.filter(
                (response) =>
                    response.quiz_ref === content._id &&
                    response.marked === true &&
                    response.result?.pass === false
            ).length;

            if (failedAttempts >= content.max_retakes) return true;
        }

        return false;
    }, [content.allow_retakes, content.max_retakes, content._id, lesson.quiz_responses, result]);

    const allQuestionsAnswered = useMemo(() => {
        return content.questions.every((question) => {
            const answerSet = studentAnswers[question._id] !== undefined;

            if (question.question_type === 'file upload') {
                if (question.min_files === 0 && !answerSet) {
                    return true;
                }
                return (
                    answerSet &&
                    (studentAnswers[question._id] as FileAnswer[]).length >= question.min_files &&
                    (studentAnswers[question._id] as FileAnswer[]).length <= question.max_files
                );
            }
            return answerSet;
        });
    }, [content.questions, studentAnswers]);

    const shouldShowRemainingAttempts = useMemo(() => {
        if (!content.allow_retakes || content.max_retakes === undefined) {
            return false;
        }

        const hasRemainingAttempts = remainingAttempts !== null && remainingAttempts > 0;
        const hasFailedResult = result && 'result' in result && result.result?.pass === false;

        return !maxRetakesReached && hasRemainingAttempts && hasFailedResult;
    }, [maxRetakesReached, remainingAttempts, result, content.allow_retakes, content.max_retakes]);

    return (
        <Pane className={styles.quiz_container}>
            <Pane>
                {(content.questions || []).map((question) => {
                    const quizResult =
                        result && 'result' in result && result.result ? result.result : null;

                    switch (question.question_type) {
                        case 'multiple choice':
                            return (
                                <MultipleChoiceQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct?.[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong?.[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'short answer':
                            return (
                                <ShortAnswerQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct?.[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong?.[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'number':
                            return (
                                <NumberQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct?.[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong?.[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'long text':
                            return (
                                <LongTextQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct?.[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong?.[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'file upload':
                            return (
                                <FileUploadQuestion
                                    quizId={content._id}
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    quizDisabled={quizDisabled}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct?.[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong?.[question._id]
                                            : undefined
                                    }
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        default:
                            return null;
                    }
                })}
            </Pane>

            {displayResult()}

            <div className={styles.submit_btn_container}>
                <Button
                    disabled={quizDisabled || !allQuestionsAnswered}
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    appearance="primary"
                >
                    {result && result.marked === true ? 'Re-submit' : 'Submit'} Answers
                </Button>
            </div>
            {result && result.marked === true && content.allow_retakes !== true ? (
                <Pane>
                    <Text>
                        You have already submitted answers and resubmissions are not allowed.
                    </Text>
                </Pane>
            ) : null}
            {maxRetakesReached ? (
                <Pane marginTop={16} marginBottom={16} textAlign="center">
                    <Text color="red">
                        You have reached the maximum number of allowed retakes (
                        {content.max_retakes}). Please contact the course provider for assistance.
                    </Text>
                </Pane>
            ) : null}
            {shouldShowRemainingAttempts && (
                <Pane marginTop={16} marginBottom={16}>
                    <Text>
                        You have {remainingAttempts}{' '}
                        {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining to pass this
                        quiz.
                    </Text>
                </Pane>
            )}
            <Pane className={styles.autoSaveContainer}>
                {lastAutoSave ? (
                    <Text>Answers last saved {new Date(lastAutoSave).toLocaleTimeString()}</Text>
                ) : null}
            </Pane>
        </Pane>
    );
};

export default QuizContent;
